import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { ProjectsService } from './projects.service';
import {
  Project,
  ProjectsResponse,
  ProjectDetail,
  CreateProjectPayload,
} from '../models/projects.model';

import { PlanningTopNavComponent } from '../../../shared/planning-nav/planning-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

type ProjectFormModel = {
  name: string;
  description: string;
};

interface ProjectFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [CommonModule, FormsModule, PlanningTopNavComponent, SearchBarComponent],
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],
})
export class ProjectsComponent implements OnInit, OnDestroy {
  projects$: Observable<Project[]> = of([]);

  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  totalPages = 1;
  pageSize = 20;
  pageSizes = [20, 50, 100];

  filters: ProjectFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentProject: Project | null = null;
  formModel: ProjectFormModel = this.getEmptyForm();

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  expandedProjectId: number | null = null;
  expandedProjectDetail: ProjectDetail | null = null;

  private readonly loadingProjectIds = new Set<number>();
  private readonly projectDetailsCache = new Map<number, ProjectDetail>();

  constructor(
    private readonly projectsService: ProjectsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.modalOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadProjects(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    this.projectsService
      .getProjects(this.currentPage, this.pageSize, {
        searchTerm: this.filters.searchTerm,
      })
      .subscribe({
        next: (res: ProjectsResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            this.projects$ = of([]);
            this.isLoading = false;
            this.cd.detectChanges();
            return;
          }

          this.totalPages = res.totalPages ?? 1;
          this.currentPage = res.currentPage ?? 1;
          this.projects$ = of(res.data ?? []);
          this.isLoading = false;
          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load projects';
          this.projects$ = of([]);
          this.isLoading = false;
          this.cd.detectChanges();
        },
      });
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.collapseExpandedProject();
    this.loadProjects();
  }

  toggleProject(project: Project): void {
    if (this.expandedProjectId === project.id) {
      this.collapseExpandedProject();
      this.cd.detectChanges();
      return;
    }

    this.expandedProjectId = project.id;
    this.expandedProjectDetail = null;

    const cachedDetail = this.projectDetailsCache.get(project.id);
    if (cachedDetail) {
      this.expandedProjectDetail = cachedDetail;
      this.cd.detectChanges();
      return;
    }

    this.loadingProjectIds.add(project.id);
    this.cd.detectChanges();

    this.projectsService
      .getProjectById(project.id)
      .pipe(
        finalize(() => {
          this.loadingProjectIds.delete(project.id);
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (detail) => {
          if (this.expandedProjectId !== project.id) {
            return;
          }

          if (detail) {
            this.projectDetailsCache.set(project.id, detail);
            this.expandedProjectDetail = detail;
          } else {
            this.expandedProjectDetail = null;
            this.showToast('Failed to load project details', 'error');
          }

          this.cd.detectChanges();
        },
        error: () => {
          if (this.expandedProjectId === project.id) {
            this.expandedProjectDetail = null;
          }

          this.showToast('Failed to load project details', 'error');
          this.cd.detectChanges();
        },
      });
  }

  isProjectExpanded(projectId: number): boolean {
    return this.expandedProjectId === projectId;
  }

  isProjectDetailLoading(projectId: number): boolean {
    return this.loadingProjectIds.has(projectId);
  }

  private collapseExpandedProject(): void {
    this.expandedProjectId = null;
    this.expandedProjectDetail = null;
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;

    this.currentPage = page;
    this.collapseExpandedProject();
    this.loadProjects();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.collapseExpandedProject();
    this.loadProjects();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentProject = null;
    this.formModel = this.getEmptyForm();

    this.modalOpen = true;
    this.syncBodyScrollLock();
  }

  openEditModal(project: Project): void {
    this.isEditMode = true;
    this.currentProject = project;

    this.formModel = {
      name: project.name,
      description: project.description ?? '',
    };

    this.modalOpen = true;
    this.syncBodyScrollLock();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentProject = null;
    this.formModel = this.getEmptyForm();

    this.syncBodyScrollLock();
  }

  submitProject(form?: NgForm): void {
    if (this.isEditMode && this.currentProject) {
      const updatePayload = {
        name: this.formModel.name,
        description: this.formModel.description,
      };

      this.projectsService.updateProject(this.currentProject.id, updatePayload).subscribe({
        next: () => {
          if (this.currentProject?.id) {
            this.projectDetailsCache.delete(this.currentProject.id);
          }

          this.closeModal();
          this.showToast('Project updated successfully', 'success');
          this.loadProjects();
          this.cd.detectChanges();
        },
        error: () => {
          this.showToast('Failed to update project', 'error');
          this.cd.detectChanges();
        },
      });

      return;
    }

    const createPayload: CreateProjectPayload = {
      name: this.formModel.name,
      description: this.formModel.description,
    };

    this.projectsService.createProject(createPayload).subscribe({
      next: () => {
        this.closeModal();
        this.showToast('Project created successfully', 'success');
        this.loadProjects();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast('Failed to create project', 'error');
        this.cd.detectChanges();
      },
    });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    this.cd.detectChanges();

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.detectChanges();
    }, 2500);
  }

  trackByProjectId(_: number, project: Project): number {
    return project.id;
  }

  trackByWorkOrderId(_: number, workOrder: any): number {
    return workOrder.id;
  }

  private getEmptyForm(): ProjectFormModel {
    return {
      name: '',
      description: '',
    };
  }
}